import React, { useState } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Visibility as VisibilityIcon } from '@mui/icons-material';
import { AuditoriaDetalle } from '../../../types/consolidatedReport.types';
import { formatDate as formatDateUtil } from '../../../utils/dateUtils';

interface AuditoriasTableProps {
  auditorias: AuditoriaDetalle[];
  timezone?: string;
}

const AuditoriasTable: React.FC<AuditoriasTableProps> = ({ auditorias, timezone }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getEstadoColor = (estado: string) => {
    switch (estado) {
      case 'completada':
        return 'success';
      case 'borrador':
        return 'warning';
      case 'sincronizada':
        return 'info';
      default:
        return 'default';
    }
  };

  const getEstadoLabel = (estado: string) => {
    switch (estado) {
      case 'completada':
        return 'Completada';
      case 'borrador':
        return 'Borrador';
      case 'sincronizada':
        return 'Sincronizada';
      default:
        return estado;
    }
  };

  const getPuntajeColor = (puntaje: number) => {
    if (puntaje >= 85) return 'success.main';
    if (puntaje >= 60) return 'warning.main';
    return 'error.main';
  };

  const formatDate = (dateString: string) => {
    return formatDateUtil(dateString, timezone);
  };

  const paginatedAuditorias = auditorias.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Paper 
      elevation={2} 
      sx={{ 
        p: isMobile ? 2 : 3, 
        mb: 3,
        width: '100%',
        overflow: 'hidden'
      }}
    >
      <Typography variant={isMobile ? 'body1' : 'h6'} gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
        Auditorías Incluidas ({auditorias.length})
      </Typography>
      
      {isMobile && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Desliza horizontalmente para ver todas las columnas →
        </Typography>
      )}

      <TableContainer
        sx={{
          '&::-webkit-scrollbar': {
            height: 8,
            width: 8,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(0,0,0,0.1)',
            borderRadius: 4,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.2)',
            borderRadius: 4,
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.3)',
            },
          },
        }}
      >
        <Table size={isMobile ? 'small' : 'medium'}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, fontSize: isMobile ? 11 : 14, minWidth: isMobile ? 80 : 120 }}>Fecha</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: isMobile ? 11 : 14, minWidth: isMobile ? 100 : 150 }}>Formulario</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: isMobile ? 11 : 14, minWidth: isMobile ? 80 : 120 }}>Auditor</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: isMobile ? 11 : 14, minWidth: isMobile ? 80 : 100 }}>Estado</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, fontSize: isMobile ? 11 : 14, minWidth: isMobile ? 60 : 80 }}>Puntaje</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, fontSize: isMobile ? 11 : 14, minWidth: isMobile ? 60 : 80 }}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedAuditorias.map((auditoria) => (
              <TableRow key={auditoria.id} hover>
                <TableCell sx={{ fontSize: isMobile ? 10 : 14 }}>{formatDate(auditoria.date)}</TableCell>
                <TableCell sx={{ fontSize: isMobile ? 10 : 14 }}>{auditoria.form}</TableCell>
                <TableCell sx={{ fontSize: isMobile ? 10 : 14 }}>{auditoria.auditor}</TableCell>
                <TableCell>
                  <Chip
                    label={getEstadoLabel(auditoria.status)}
                    color={getEstadoColor(auditoria.status) as any}
                    size="small"
                    sx={{ fontSize: isMobile ? 9 : 12 }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: getPuntajeColor(auditoria.score),
                      fontSize: isMobile ? 11 : 14
                    }}
                  >
                    {auditoria.score.toFixed(1)}%
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Ver detalle en nueva pestaña">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => window.open(`/audits/${auditoria.id}`, '_blank')}
                    >
                      <VisibilityIcon fontSize={isMobile ? 'small' : 'medium'} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={isMobile ? [5, 10] : [5, 10, 25, 50]}
        component="div"
        count={auditorias.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage={isMobile ? "Filas:" : "Filas por página:"}
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        sx={{
          '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
            fontSize: isMobile ? 11 : 14
          }
        }}
      />
    </Paper>
  );
};

export default AuditoriasTable;
